/**
 * /policies/payroll/tax-withholding — Session 3c PR 20.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { TaxWithholdingItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function PayrollTaxWithholdingPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-payroll-tax", le],
    queryFn: () => session3cApi.listTaxWithholding(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<TaxWithholdingItem>[] = [
    {
      headerKey: "policies.payroll_tax.col.jurisdiction",
      label: "Jurisdiction",
      render: (r) => <span className="text-xs">{r.jurisdiction}</span>,
    },
    {
      headerKey: "policies.payroll_tax.col.type",
      label: "Tax type",
      render: (r) => <span className="text-xs text-text-secondary">{r.tax_type}</span>,
    },
    {
      headerKey: "policies.payroll_tax.col.rate",
      label: "Rate %",
      render: (r) => <span className="font-mono">{r.rate_pct.toFixed(2)}%</span>,
    },
    {
      headerKey: "policies.payroll_tax.col.threshold",
      label: "Threshold",
      render: (r) => <span className="font-mono text-xs">{r.threshold_amount ?? "—"}</span>,
    },
  ];

  return (
    <PolicyListPage<TaxWithholdingItem>
      title={t("policies.payroll_tax.title", "Tax / Withholding")}
      subtitle={t("policies.payroll_tax.subtitle", "Per-jurisdiction tax tables (KSA today — no personal income tax for residents · UAE/Egypt for future v2 jurisdictions).")}
      agent="mutabaa"
      domain="payroll_tax"
      newButtonLabel={t("policies.payroll_tax.cta.new", "New Tax Rule")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.payroll_tax.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.payroll_tax.suggestion.clean_body", "KSA: no personal income tax for residents. GOSI handled separately. Expat tax obligations tracked when applicable."),
          primaryActionLabel: t("policies.payroll_tax.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "payroll-tax-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.payroll_tax.title", "Tax / Withholding"),
        window_label: t("policies.payroll_tax.window", "KSA jurisdiction"),
      }}
    />
  );
}
